/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';

import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';

import {Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {
  ApiResponse,
  Assignee,
  BaseComponent,
  Board,
  Column,
  CrmIssueParent,
  FileService,
  IssueType,
  Picture,
  StoreService,
  Task
} from 'compob2b-libs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {IssueModalComponent} from './issue-modal/issue-modal.component';
import {NbDialogRef, NbPopoverDirective} from '@nebular/theme';
import {translations} from '../../../../../environments/translations.ru';
import {AuthService} from 'compob2b-auth';
import {MenuService} from '../../../menu/menu.service';
export interface ProjectIdIssueId {
  projectId: number;
  issueId: number;
}
@Component({
    templateUrl: './project-show.component.html',
    styleUrls: ['./project-show.component.scss', 'issue-tag/issue-tag.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectShowComponent extends BaseComponent implements OnInit, OnDestroy {
  board: Board;
  syncColumn: string[] = [];
  projectId = 0;
  issueId: number;
  selectedIssue = 0;
  private last: MouseEvent;
  private mouseDown = false;
  private dialogRef: NbDialogRef<IssueModalComponent>;
  public readonly translations = translations;

  private menuService = inject(MenuService);
  private widgetsContent: ElementRef;

  @ViewChild('deskBoard', {static: false}) set content(content: ElementRef) {
    if (content) {
      this.widgetsContent = content;
    }
  }

  @ViewChildren(NbPopoverDirective) popovers: QueryList<NbPopoverDirective>;

  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    private readonly fileService: FileService,
    protected storeService: StoreService,
    private readonly authService: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.storeService.updatedIssue$
        .pipe(
            filter(update => !!update),
            takeUntil(this.unsubscribe)
        )
        .subscribe(update => {
          if (update && this.board) {
            this.updateIssueInBoard(update.id, update.data);
          }
        });

        this.route.queryParams.pipe(
          filter((params: Params) => 'issueId' in params),
          tap((params: Params) => this.issuePopupInit(params.issueId)),
          takeUntil(this.unsubscribe)
        ).subscribe();

        this.route.params.pipe(
          filter((params: Params) => 'id' in params),
          filter((params: Params) => params.id.match(/^[A-Za-z0-9]+-\d+$/)),
          switchMap((params: Params) => this.getProjectIdIssueId(params.id)),
          tap((projectIdIssueId: ProjectIdIssueId) => {
            this.projectId = projectIdIssueId.projectId;
            this.issueId = projectIdIssueId.issueId;
          }),
          switchMap((projectIdIssueId: ProjectIdIssueId) => this.getBoard(projectIdIssueId.projectId)),
          switchMap(_ => this.route.queryParams.pipe(
            tap((params: Params) => {
              this.filterIssues(params);
            })
          )),
          tap(() => this.issuePopupInit(this.issueId)),
          takeUntil(this.unsubscribe)
        ).subscribe();


        this.route.params.pipe(
          filter((params: Params) => 'id' in params),
          filter((params: Params) => params.id.match(/^\d+$/)),
          switchMap((params: Params) => this.getBoard(params.id)),
          switchMap(_ => this.route.queryParams.pipe(
            tap((params: Params) => {
              this.filterIssues(params);
            })
          )),
          takeUntil(this.unsubscribe)
        ).subscribe();

  }

  getProjectIdIssueId(code: string): Observable<ProjectIdIssueId> {
    return this.compoApiService.get<ProjectIdIssueId>('/projects/code/' + code).pipe(map(_ => _.data));
  }

  ngOnDestroy() {
    this.menuService.destroyForProject();
  }

  public isParentAnEpic(issue: CrmIssueParent): boolean {
    return issue !== null && issue.crmIssueType.id === IssueType.EPIC.valueOf();
  }

  private getBoard(projectId: number): Observable<any> {
    if (null !== this.board && undefined !== this.board && projectId == this.board.id) {
      return of(this.board);
    }

    this.projectId = projectId;

    return this.compoApiService.get<Board>('/projects/show/' + this.projectId).pipe(
      tap((resp: ApiResponse<Board>) => {
        this.board = resp.data;
        this.board.columns.forEach(column => {
          column.headerDisabled = true;
        });

        this.storeService.setTitle(this.board.header);
        this.menuService.initForProject(this.board.id, this.board.header);
        this.board.columns.forEach((q) => this.syncColumn.push(q.id + ''));
      }),
      catchError(error => {
        let errorStr = error.status;
        if (error.status == 403) {
          errorStr = translations.projectShowDeny;
        }

        this.handleError(errorStr);
        return of(error);
      }),
    );
  }

  isClientRole(): boolean {
    return this.authService.isClient();
  }

  public dropGrid(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.board.columns,
      event.previousIndex,
      event.currentIndex
    );
    const newSort = [];
    this.board.columns.forEach((item, key) =>
      newSort.push({id: item.id, sort: key})
    );
    this.compoApiService
      .post('/columns/save-pos', newSort)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe();
  }

  public drop(event: CdkDragDrop<any>): void {
    const column = this.board.columns.find(item => item.id == event.container.id as unknown as number);
    if (column.columnType > 2 && event.item.data.estimate === 0) {
      this.warningToast('Оцените задачу перед сменой статуса');
      return;
    }
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      const newSort = [];
      event.container.data.forEach((item, key) =>
        newSort.push({id: item.id, sort: key})
      );
      this.compoApiService
        .post('/columns/save-issues-pos', newSort)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
    } else {
      const task: Task = event.item.data;
      const taskIndex = event.previousContainer.data.findIndex(itemTask => itemTask.id === task.id);
      if (taskIndex === -1) {
        return;
      }

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        taskIndex,
        event.currentIndex
      );

      const newSortPreviousContainer = [];
      const newSortContainer = [];
      const newSortContainerId = event.container.id;
      const newSortPreviousContainerId = event.previousContainer.id;

      event.container.data.forEach((item, key) =>
        newSortContainer.push({id: item.id, sort: key})
      );
      event.previousContainer.data.forEach((item, key) =>
        newSortPreviousContainer.push({id: item.id, sort: key})
      );
      this.compoApiService
        .post('/columns/save-issues-and-columns-pos', {
          newSortContainer,
          newSortContainerId,
          newSortPreviousContainer,
          newSortPreviousContainerId,
          issueId: event.container.data[event.currentIndex].id,
        })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
    }
  }

  getImageUri(picture: Picture, width: number = 96): string {
    return null === picture ? null : this.fileService.getImageUri(picture, width);
  }

  getProjectImageUri(picture: Picture, width: number = 200): string {
    return this.fileService.getImageUri(picture, width);
  }

  removeColumn(columnId: number) {
    const column = this.board.columns.find(item => item.id == columnId as unknown as number);
    if (column.columnType !== null) {
      alert('Нельзя удалить колонку по умолчанию');
      return;
    }

    this.compoApiService
      .delete('/columns/' + columnId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.board.columns = this.board.columns.filter(
          (value) => value.id !== columnId
        );
        this.cdr.detectChanges();
      });
  }

  updateHeader(column: Column) {
    if (column.header) {
      this.compoApiService
        .patch('/columns/save-header/' + column.id, {header: column.header})
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
      column.headerDisabled = true;
    }
  }

  openIssueModal(iid: number, alias: string, $event) {
    this.router.navigate(['cabinet','crm','project','show', alias + '-' + iid], {queryParamsHandling: 'merge'}).then();
    $event.stopPropagation();
  }

  private issuePopupInit(issueId: number) {
    if (issueId == this.selectedIssue) {
      return;
    }
    if (this.dialogRef) {
      this.selectedIssue = 0;
      this.dialogRef.close();
    }
    if (!issueId) {
      return;
    }
    this.selectedIssue = issueId;

    this.dialogRef = this.dialogService.open(IssueModalComponent, {
      context: {
        issueId: issueId,
        setTitle: true,
      },
      closeOnEsc: false,
      dialogClass: 'issue-modal-dialog',
      backdropClass: 'issue-modal-back',
      closeOnBackdropClick: false,
      autoFocus: true,
    });

    this.dialogRef.onClose.subscribe(_ => {
      this.router.navigate(['cabinet','crm','project','show', this.projectId], {queryParams: {issueId: null, img: null}, queryParamsHandling: 'merge'}).then();
      this.storeService.setTitle(this.board.header);
      this.selectedIssue = 0;
      this.issueId = null;
    });

    this.dialogRef.componentRef.instance.onUpdate.pipe(
      switchMap(_ => this.getBoard(this.projectId)),
      take(1)
    ).subscribe();
  }

  private filterIssues(params: Params) {
    if (this.board !== undefined) {

      this.board.columns.forEach(column => {
        column.tasks.forEach(task => {
          if (params.assignee === 'none') {
            task.hidden = task.assignee !== null;
          } else {
            if (params?.assignee) {

              task.hidden = task.assignee?.uid !== params.assignee;

              const assigneeIdMatch = params.assignee.match(/\d+/);
              if (assigneeIdMatch && task.assignee?.id === parseInt(assigneeIdMatch[0])) {
                task.hidden = false;
              }

              if (task.relatedPersons && task.relatedPersons.some((item: Assignee) => item?.uid === params.assignee)) {
                task.hidden = false;
              }
            } else {
              task.hidden = false;
            }
          }

          if (!task.hidden && params.payment) {
            const paymentId = parseInt(params.payment, 10);
            if (paymentId === 0) {
              task.hidden = task.subprojectId !== null;
            } else {
              task.hidden = task?.subprojectId !== paymentId;
            }
          }

          const taskCreateDate = task.createdAt.toString().substring(0, 10);
          const taskReleaseDate = task.releasedAt ? task.releasedAt.toString().substring(0, 10) : '';

          if (!task.hidden) {
            if (params.from) {
              this.hideIf(task, taskCreateDate < params.from);
            }
            if (params.to) {
              this.hideIf(task, taskCreateDate > params.to);
            }
            if (params.rfrom) {
              this.hideIf(task, !taskReleaseDate || taskReleaseDate < params.rfrom);
            }
            if (params.rto) {
              this.hideIf(task, !taskReleaseDate || taskReleaseDate > params.rto);
            }

            if (params.term) {
              this.hideIf(task, this.filterByIssueName(task, params.term));
            }

            if (params.epic) {
              if (params.epic == 'Не назначено') {
                this.hideIf(task, !!task.parent?.epicName);
              } else {
                this.hideIf(task, task.parent?.epicName != params.epic);
              }
            }

            if (params.tag) {
              if (params.tag == 'Не назначено') {
                this.hideIf(task, !!task.tag);
              } else {
                this.hideIf(task, task.tag?.header != params.tag);
              }
            }

            if (params.environment) {
              if (params.environment === 'Не назначено') {
                this.hideIf(task, task.environmentList.length > 0);
              } else {
                this.hideIf(task, !task.environmentList.includes(params.environment));
              }
            }
          }
        });
      });
    }

    this.cdr.detectChanges();
    this.widgetsContent.nativeElement.addEventListener('contextmenu', this.onRightClick.bind(this));
  }

  private hideIf(task, iff: boolean): void {
    if (iff) {
      task.hidden = true;
    }
  }

  private filterByIssueName(task: Task, term: string): boolean {

    const nameMatch = task.header.toLowerCase().indexOf(term.toLowerCase()) === -1;
    const codeMatch = (task.projectAlias + '-' + task.iid).toLowerCase().indexOf(term.toLowerCase()) === -1;
    return nameMatch && codeMatch;
  }

  toggleProjectFavorites() {
    const isFavorite = !this.board.favorite;
    const uri = `/projects/${this.board.id}/favorites`;

    const stream$: Observable<ApiResponse<any>> = isFavorite
      ? this.compoApiService.patch<boolean, any>(uri, isFavorite)
      : this.compoApiService.delete<any>(uri);

    stream$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.board.favorite = isFavorite;
        this.cdr.detectChanges();
      });
  }

  @HostListener('document:keydown.ArrowRight')
  private onRightHandler() {
    this.widgetsContent.nativeElement.scrollLeft += 150;
  }

  @HostListener('document:keydown.ArrowLeft')
  private onLeftHandler() {
    this.widgetsContent.nativeElement.scrollLeft -= 150;
  }

  @HostListener('mouseup')
  private onMouseUp() {
    this.mouseDown = false;
  }

  @HostListener('mousemove', ['$event'])
  private onMouseMove(event: MouseEvent) {
    if (this.mouseDown) {
      this.widgetsContent.nativeElement.scrollLeft -= event.clientX - this.last.clientX;
      this.last = event;
    }
  }

  private onRightClick(event) {
    event.preventDefault();
    this.mouseDown = true;
    this.last = event;
  }

  showEstimateTime(n: number): string {
    return 0 === n ? '-' : Math.trunc((n / 60) * 100) / 100 + 'ч';
  }

  closeColumnRemoveConfirm() {
    this.popovers.forEach(pop => {
      pop.hide();
    });
  }

  getAssigneeName(item) {
    return item.assignee?.name ?? 'Не назначено';
  }

  setEnvironment(env: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.router.navigate([], {queryParams: {environment: env}, queryParamsHandling: 'merge'});
  }

  private updateIssueInBoard(issueId: number, issueData: any): void {


    if (!this.board) return;

    const destinationColumnId = issueData.crmColumn.id;

    for (let i = 0; i < this.board.columns.length; i++) {
      const column = this.board.columns[i];
      const taskIndex = column.tasks.findIndex(task =>
          task.id === issueId ||
          (task.iid === issueData.iid && task.projectAlias === issueData.projectAlias)
      );

      if (taskIndex !== -1) {

        if (!issueData.enabled) {
          column.tasks.splice(taskIndex, 1);
          break;
        }

        const task = column.tasks[taskIndex];
        const wasHidden = task.hidden;

        const currentColumnId = column.id;
        const columnChanged = destinationColumnId && currentColumnId !== destinationColumnId;

        let updatedAssignee = null;
        if (issueData.assignee) {
          updatedAssignee = this.normalizeAssignee(issueData.assignee, task.assignee);
        }

        const updatedTask = {
          ...task,
          ...issueData,
          assignee: updatedAssignee,
          subprojectId: issueData.subproject ? issueData.subproject.id : null,
          hidden: wasHidden
        };

        if (columnChanged) {

          column.tasks.splice(taskIndex, 1);

          const destinationColumn = this.board.columns.find(col => col.id === destinationColumnId);

          if (destinationColumn) {
            destinationColumn.tasks.unshift(updatedTask);
          }
        } else {
          column.tasks[taskIndex] = updatedTask
        }
        break;
      }
    }
    console.log('updateIssueInBoard', this.board )
    this.cdr.markForCheck();
  }

  private normalizeAssignee(assigneeData: any, taskAssignee: Assignee): any {
    if (!assigneeData) return null;

    return {
      id: assigneeData.id,
      uid: taskAssignee.uid,
      name: assigneeData.name || `${assigneeData.firstName || ''} ${assigneeData.lastName || ''}`.trim(),
      avatar: assigneeData.avatar || assigneeData.picture,
      ...assigneeData
    };
  }

  getColumnColor(column: any): string {
    const storedColor = localStorage.getItem(`column-${column.id}`);
    if (storedColor) {
      return storedColor;
    }

    const header = column.header?.toLowerCase();

    if (header?.includes('очередь')) return '#666666';
    if (header?.includes('оценку')) return '#CC8CFF';
    if (header?.includes('оценкой')) return '#CCCC99';
    if (header?.includes('к выполнению')) return '#FFCC00';
    if (header?.includes('в работе')) return '#FF3300';
    if (header?.includes('тестирование')) return '#0099FF';
    if (header?.includes('готово')) return '#00CC00';

    return null;
  }
}
